import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Popover } from 'antd';
import { useHiddenColumns } from '../table/useHiddenColumns';
import { filterRows, ownerOptions, sortRows, stateOptions } from '../../utils/workspacePageModel';

/**
 * The tools of a workspace's project lists (the Projects tab's table and the Overview's Projects gadget), so both
 * filter, sort and show columns the same way: a search, State, Owner (Me, Others, each person), "Has problems" (a
 * quality check blocks Publish, or a build failed), sortable column headers and Columns ▾ (remembered per browser).
 * Rows are utils/workspacePageModel.js buildRows rows.
 */

export const NO_FILTERS = { search: '', state: 'all', owner: 'all', problems: 'all' };

/** The filters and the sort of a list, and its rows filtered and sorted: `{ filters, setFilter, sort, sortBy, rows }`. */
export function useProjectListView(all) {
  const [filters, setFilters] = useState(NO_FILTERS);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const setFilter = useCallback((key, value) => setFilters((now) => ({ ...now, [key]: value })), []);
  const sortBy = useCallback((key) => setSort((now) => (now.key === key ? { key, dir: now.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })), []);
  const rows = useMemo(() => sortRows(filterRows(all, filters), sort.key, sort.dir), [all, filters, sort]);
  const filtered = Object.keys(NO_FILTERS).some((key) => filters[key] !== NO_FILTERS[key]);
  return { filters, setFilter, sort, sortBy, rows, filtered, clear: () => setFilters(NO_FILTERS) };
}

export function ProjectFilters({ all, filters, setFilter, label = 'projects' }) {
  return (
    <>
      <input aria-label={`Search ${label}`} className="ws-input ws-grow" onChange={(event) => setFilter('search', event.target.value)} placeholder="Search projects" type="search" value={filters.search} />
      <select aria-label="State" className="ws-input" onChange={(event) => setFilter('state', event.target.value)} value={filters.state}>
        <option value="all">State: all</option>
        {stateOptions(all).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <select aria-label="Owner" className="ws-input" onChange={(event) => setFilter('owner', event.target.value)} value={filters.owner}>
        <option value="all">Owner: all</option>
        {ownerOptions(all).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
      <button
        aria-pressed={filters.problems === 'problems'}
        className={`ws-btn${filters.problems === 'problems' ? ' is-on' : ''}`}
        onClick={() => setFilter('problems', filters.problems === 'problems' ? 'all' : 'problems')}
        title="Only the projects where a quality check blocks Publish, or a build failed"
        type="button"
      >
        Has problems
      </button>
    </>
  );
}

ProjectFilters.propTypes = {
  all: PropTypes.array.isRequired,
  filters: PropTypes.object.isRequired,
  label: PropTypes.string,
  setFilter: PropTypes.func.isRequired,
};

/** Which columns are hidden, remembered per browser: the tables' shared hook (components/table). */
export { useHiddenColumns };

export function ColumnsMenu({ columns, show, toggle }) {
  return (
    <Popover
      content={(
        <fieldset className="ws-columns" data-testid="ws-columns-menu">
          <legend className="ws-sr">Columns to show</legend>
          {columns.slice(1).map((column) => (
            <label className="ws-check" key={column.key} style={{ display: 'flex', gap: 8, minHeight: 36, alignItems: 'center' }}>
              <input checked={show(column.key)} onChange={() => toggle(column.key)} type="checkbox" /> {column.label}
            </label>
          ))}
        </fieldset>
      )}
      placement="bottomRight"
      trigger="click"
    >
      <button aria-haspopup="true" className="ws-btn" type="button">Columns ▾</button>
    </Popover>
  );
}

ColumnsMenu.propTypes = { columns: PropTypes.array.isRequired, show: PropTypes.func.isRequired, toggle: PropTypes.func.isRequired };

/** A sortable column header: ↑ / ↓ on the sorted one, ↕ on the others; `aria-sort` says which. */
export function SortHeader({ column, sort, onSort }) {
  const on = sort.key === column.key;
  return (
    <th aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'} scope="col">
      <button className="ws-sort" onClick={() => onSort(column.key)} type="button">
        {column.label}<span aria-hidden="true">{on ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
      </button>
    </th>
  );
}

SortHeader.propTypes = { column: PropTypes.object.isRequired, onSort: PropTypes.func.isRequired, sort: PropTypes.object.isRequired };
